import React from "react";
import { IForm } from "../../../../types/form.type";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { useAlertContext } from "../../../../contexts/alert/AlertProvider";
import { Controller, useForm } from "react-hook-form";
import { asyncPostData, asyncPutData } from "../../../../utils/httpRequests";
import ModalForm from "../../modal/ModalForm";
import { Stack } from "@mui/material";
import SelectApiInput from "../../input/SelectApiInput";
import TextInput from "../../input/TextInput";
import FormNHOMBAN from "../nhomban/FormNHOMBAN";

const schema = yup.object({
  ma_ban: yup.string().required("Vui lòng nhập mã bàn"),
  ten_ban: yup.string().required("Vui lòng nhập tên bàn"),
  kho: yup
    .object()
    .typeError(`Vui lòng chọn chi nhánh`)
    .required(`Vui lòng chọn chi nhánh`),
});

function FormDMBAN({
  defaultValues,
  isEdit,
  apiCode,
  open,
  onClose,
  setLoad,
}: IForm) {
  const app = useSelector((state: any) => state.app.data);
  const token = useSelector((state: any) => state.auth.token);
  const { showAlert } = useAlertContext();
  const defaultData: { [key: string]: any } = {
    ma_ban: "",
    ten_ban: "",
    kho: null,
    nhom_ban: null,
  };
  const originData = defaultValues
    ? {
        ...defaultData,
        ...defaultValues,
        kho: defaultValues.ma_kho
          ? {
              ma_kho: defaultValues.ma_kho || "",
              ten_kho: defaultValues.ten_kho || "",
            }
          : null,
        nhom_ban: defaultValues.nh_ban
          ? {
              _id: defaultValues.nh_ban || "",
              group_name: defaultValues.ten_nh_ban || "",
            }
          : null,
        trang_thai: defaultValues.trang_thai
          ? {
              ma_trang_thai: defaultValues.trang_thai || "",
              ten_trang_thai: defaultValues.ten_trang_thai || "",
            }
          : null,
      }
    : defaultData;

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: "onSubmit",
    defaultValues: originData,
    resolver: yupResolver(schema),
  });

  const handleReset = () => {
    reset(originData);
  };

  const generateDataPost = async (values: typeof defaultData) => {
    const { kho, nhom_ban, trang_thai, ...fields } = values;
    return {
      ...fields,
      ma_kho: kho?.ma_kho || "",
      ten_kho: kho?.ten_kho || "",
      nh_ban: nhom_ban?._id || "",
      ten_nh_ban: nhom_ban?.group_name || "",
      trang_thai: trang_thai?.ma_trang_thai || "0",
    };
  };

  const handleSave = async (values: typeof defaultData) => {
    const dataPost: { [key: string]: any } = await generateDataPost(values);
    let resp = null;
    if (isEdit) {
      resp = await asyncPutData({
        id: dataPost._id,
        apiCode,
        token,
        idApp: app?._id,
        data: dataPost,
      });
    } else {
      resp = await asyncPostData({
        apiCode,
        token,
        idApp: app?._id,
        data: dataPost,
      });
    }
    if (resp.status !== 200) {
      showAlert({
        type: "error",
        message:
          resp?.data?.error || resp?.data?.message || "Lỗi khi lưu đối tượng",
      });
    } else {
      onClose();
      setLoad?.((prev: number) => prev + 1);
    }
  };

  const onSubmit = handleSubmit(handleSave);

  return (
    <ModalForm
      formWidth="500px"
      formTitle="bàn"
      loading={isSubmitting}
      open={open}
      isEdit={isEdit}
      onClose={onClose}
      onSubmit={onSubmit}
      onReset={handleReset}
    >
      <Stack gap={2}>
        <TextInput
          required
          label="Mã bàn"
          labelWidth="25%"
          placeholder="Mã bàn"
          name="ma_ban"
          register={register}
          errorMessage={errors?.ma_ban?.message as string}
        />
        <TextInput
          required
          label="Tên bàn"
          labelWidth="25%"
          placeholder="Tên bàn"
          name="ten_ban"
          register={register}
          errorMessage={errors?.ten_ban?.message as string}
        />
        <Controller
          name="kho"
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <SelectApiInput
                required
                labelWidth="25%"
                label="Chi nhánh"
                placeholder="Chọn chi nhánh"
                apiCode="dmkho"
                searchFields={["ten_kho"]}
                getOptionLabel={(option) => option.ten_kho}
                checkEqual={(value, option) => value?.ma_kho === option?.ma_kho}
                value={value}
                onChange={onChange}
                errorMessage={errors?.kho?.message as string}
              />
            );
          }}
        />
        <Controller
          name="nhom_ban"
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <SelectApiInput
                labelWidth="25%"
                label="Khu vực bàn"
                placeholder="Chọn khu vực bàn"
                apiCode="group"
                searchFields={["group_name"]}
                defaultCondition={{ group_type: "TABLE" }}
                getOptionLabel={(option) => option.group_name}
                checkEqual={(value, option) => value?._id === option?._id}
                value={value}
                onChange={onChange}
                Form={FormNHOMBAN}
              />
            );
          }}
        />
        {isEdit && (
          <Controller
            name="trang_thai"
            control={control}
            render={({ field: { value, onChange } }) => {
              return (
                <SelectApiInput
                  labelWidth="25%"
                  label="Trạng thái"
                  placeholder="Chọn trạng thái"
                  apiCode="trangthai"
                  withIdApp={false}
                  searchFields={["ma_trang_thai", "ten_trang_thai"]}
                  defaultCondition={{ ma_ct: "DMBAN" }}
                  getOptionLabel={(option) =>
                    option.ten_trang_thai || option.ma_trang_thai
                  }
                  checkEqual={(value, option) =>
                    value?.ma_trang_thai === option?.ma_trang_thai
                  }
                  value={value}
                  onChange={onChange}
                />
              );
            }}
          />
        )}
      </Stack>
    </ModalForm>
  );
}

export default FormDMBAN;
